import Link from 'next/link';
import { cn } from '@/src/shared/lib/utils';
import { LinkButton } from '../button';
import { Typography } from '@/src/shared/ui/typography/Typography';

interface NavigationItem {
  label: string;
  href?: string;
  scrollTo?: string;
  onClick?: () => void;
}

interface NavigationProps {
  items: NavigationItem[];
  pathname: string;
  activeSection?: string;
}

export function Navigation({ items, pathname, activeSection }: NavigationProps) {
  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    item: NavigationItem
  ) => {
    if (item.onClick) {
      e.preventDefault();
      item.onClick();
    } else if (item.scrollTo) {
      e.preventDefault();
      const element = document.getElementById(item.scrollTo);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  };

  return (
    <nav className="flex items-center gap-6 xl:gap-8">
      {items.map((item, index) => {
        // Check if this item is active based on section scroll or pathname
        const isActive = activeSection
          ? item.scrollTo === activeSection
          : pathname === item.href;

        // If onClick handler exists, render as button
        if (item.onClick) {
          return (
            <button
              key={item.label || index}
              onClick={item.onClick}
              className={cn(
                'inline-flex items-center justify-center transition-colors hover:text-accent-1',
                'bg-transparent border-none cursor-pointer',
                isActive ? 'text-accent-1' : 'text-text-primary'
              )}
            >
              <Typography variant="h3" color="inherit" className="whitespace-nowrap">
                {item.label}
              </Typography>
            </button>
          );
        }

        // Otherwise, render as link
        return (
          <LinkButton
            key={item.href || item.label || index}
            href={item.href || '#'}
            onClick={(e) => handleClick(e, item)}
            className={cn(
              'transition-colors hover:text-accent-1',
              isActive
                ? 'text-accent-1'
                : 'text-text-primary'
            )}
          >
            {item.label}
          </LinkButton>
        );
      })}
    </nav>
  );
}
